<template>
  <div class="add-processing">
    <VToolbar
      flat
      color="backgroundDiv"
    >
      <VRow justify="center" class="mx-n12">
        <VCol class="shrink">
          <VBtn
            icon
            variant="text"
            size="small"
            color="grey-darken-2"
            @click="prev"
          >
            <VIcon>
              {{ icons.mdiChevronLeft }}
            </VIcon>
          </VBtn>
        </VCol>
        <VCol class="text-center shrink">
          <VToolbarTitle v-if="calendar">
            <h3 data-testid="portal-events-calendar-title">{{ calendar.title }}</h3>
          </VToolbarTitle>
        </VCol>
        <VCol class="shrink">
          <VBtn
            icon
            variant="text"
            size="small"
            color="grey-darken-2"
            @click="next"
          >
            <VIcon>
              {{ icons.mdiChevronRight }}
            </VIcon>
          </VBtn>
        </VCol>
      </VRow>
    </VToolbar>
    <VCalendar
      ref="calendar"
      v-model="calendarDate"
      class="hidden-xs"
      data-testid="portal-events-calendar-grid"
      :show-month-on-first="false"
      :locale="localeStore.culture"
      color="primary"
      @change="fetchEvents"
    >
      <template #day-label="{ day, month }">
        <span v-if="currentMonth === month">{{ day }}</span>
        <span v-else />
      </template>
      <template #day="{ date }">
        <div class="text-center">
          <VRow class="fill-height">
            <VCol class="event-slot">
              <span v-if="hasEvent(date)" class="calendar-event-dot" />
            </VCol>
          </VRow>
        </div>
      </template>
    </VCalendar>
    <SLoadingOverlay :loading="loading" />
  </div>
</template>
<script setup lang="ts">
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js'
import { useEventStore } from '~/stores/events'
import { useAction } from '~/composables/action'
import { useLocaleStore } from '~/stores/locale'

const localeStore = useLocaleStore()

type CalendarChange = {
  start: {
    month: number
    year: number
  }
}

const calendar = ref<{ title?: string; prev: () => void; next: () => void } | null>(null)
const eventStore = useEventStore()
const prev = () => calendar.value?.prev()
const next = () => calendar.value?.next()

const events = computed(() => eventStore.events)

const currentMonth = ref(0)
const calendarDate = ref(new Date().toISOString().slice(0, 10))
const hasEvent = (date: string) => {
  return !!events.value.find((e) => {
    return e.eventStart === date
  })
}

const { loading, processAction } = useAction()
const fetchEvents = ({ start }: CalendarChange) => {
  processAction(async () => {
    await eventStore.queryEvents(start.month, start.year)
    currentMonth.value = start.month
  })
}

onMounted(() => {
  const today = new Date()
  fetchEvents({ start: { month: today.getMonth() + 1, year: today.getFullYear() } })
})

const icons = { mdiChevronLeft, mdiChevronRight }
</script>

<style lang="scss" scoped>
h3 {
  color: rgb(var(--v-theme-on-surface));
}

.event-slot {
  min-height: 25px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.calendar-event-dot {
  width: 9px;
  height: 9px;
  margin-top: -2px;
  border-radius: 999px;
  background-color: rgb(var(--v-theme-header));
}
:deep() {
  .v-calendar-weekly__day, .v-calendar-weekly__head-weekday, .v-calendar-weekly {
    border: none;
  }
  .v-calendar-weekly__week,
  .v-calendar-weekly__day {
    height: 28px;
  }
  .v-calendar-weekly__week:has(.calendar-event-dot),
  .v-calendar-weekly__week:has(.calendar-event-dot) .v-calendar-weekly__day {
    height: 38px;
  }
  .v-calendar-weekly__week:has(.calendar-event-dot) .event-slot {
    min-height: 35px;
  }
  .v-calendar-weekly__head-weekday {
    color: rgb(var(--v-theme-greyedOut));
  }
  .v-calendar-weekly {
    background-color: rgb(var(--v-theme-backgroundDiv));
  }
  .v-calendar-weekly__day.v-outside,
  .v-calendar-weekly__head-weekday.v-outside {
    background-color: rgb(247, 247, 247);
  }
  .v-calendar-weekly__day.v-outside {
    color: rgb(0, 0, 0);
  }
}
</style>
